import * as React from "react";

import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import { Button, Checkbox, Group, Modal, Stack, Text, TextInput } from "@mantine/core";

import { useNavigate } from "react-router";
import * as authActions from "../../data/actions/authenticationActions";
import { getSelf } from "../../data/actions/personActions";
import { PrivacyLink } from "../common/Links";
import TextSeparator from "../common/TextSeparator";
import ResetPasswordForm from "./TriggerResetPasswordForm";
// import SignInWithAppleButton from '../apple/SignIn';

interface ILoginFormProps {
  actions: any;

  navigate?: any;
  code: any; // fb token atm
  redirectOnLogin: string;
  callback: () => void;
}

interface ILoginFormState {
  username: string;
  password: string;
  remember: boolean;
  error?: string | null;
  loading?: boolean;
  resetPassword: {
    show: boolean;
    email?: string;
  };
}

const LoginForm: React.FunctionComponent<ILoginFormProps> = (props) => {
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [remember, setRemember] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [showResetPassword, setShowResetPassword] = React.useState(false);

  const navigate = useNavigate();

  const loginButtonClicked = (event) => {
    event.preventDefault();
    setLoading(true);
    props.actions
      .login(username, password, remember)
      .then(() => {
        if (props.callback) {
          props.callback();
        } else {
          navigate?.(props.redirectOnLogin || "/");
        }
      })
      .catch((er) => {
        setLoading(false);
        setError(er.message);
      });
  };

  return (
    <>
      <form onSubmit={loginButtonClicked}>
        <Stack gap="md">
          <TextInput
            label="Username"
            placeholder="Enter your username"
            type="email"
            name="username"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.currentTarget.value)}
            disabled={loading}
            size="md"
            styles={{
              label: {
                color: "var(--color-text-medium)",
                fontSize: "14px",
                fontWeight: 500,
                marginBottom: "6px"
              },
              input: {
                backgroundColor: "rgba(0, 0, 0, 0.2) !important",
                border: "1px solid rgba(255, 255, 255, 0.15) !important",
                borderRadius: "12px !important",
                color: "var(--color-text-primary) !important",
                fontSize: "15px",
                padding: "12px 16px",
                minHeight: "44px",
                boxShadow: "none !important",
                "&::placeholder": {
                  color: "rgba(255, 255, 255, 0.5) !important"
                },
                "&:focus": {
                  borderColor: "var(--color-brand-medium) !important",
                  backgroundColor: "rgba(0, 0, 0, 0.25) !important",
                  outline: "none !important",
                  boxShadow: "none !important"
                },
                "&:hover": {
                  borderColor: "rgba(255, 255, 255, 0.25) !important",
                  backgroundColor: "rgba(0, 0, 0, 0.2) !important"
                }
              }
            }}
          />

          <TextInput
            label="Password"
            placeholder="Enter your password"
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            disabled={loading}
            size="md"
            styles={{
              label: {
                color: "var(--color-text-medium)",
                fontSize: "14px",
                fontWeight: 500,
                marginBottom: "6px"
              },
              input: {
                backgroundColor: "rgba(0, 0, 0, 0.2) !important",
                border: "1px solid rgba(255, 255, 255, 0.15) !important",
                borderRadius: "12px !important",
                color: "var(--color-text-primary) !important",
                fontSize: "15px",
                padding: "12px 16px",
                minHeight: "44px",
                boxShadow: "none !important",
                "&::placeholder": {
                  color: "rgba(255, 255, 255, 0.5) !important"
                },
                "&:focus": {
                  borderColor: "var(--color-brand-medium) !important",
                  backgroundColor: "rgba(0, 0, 0, 0.25) !important",
                  outline: "none !important",
                  boxShadow: "none !important"
                },
                "&:hover": {
                  borderColor: "rgba(255, 255, 255, 0.25) !important",
                  backgroundColor: "rgba(0, 0, 0, 0.2) !important"
                }
              }
            }}
          />

          <Group justify="space-between" mt="sm">
            <Checkbox
              checked={remember}
              onChange={(e) => setRemember(e.currentTarget.checked)}
              label="Remember me"
              size="sm"
              styles={{
                label: {
                  color: "var(--color-text-medium)",
                  fontSize: "13px"
                },
                input: {
                  backgroundColor: "rgba(0, 0, 0, 0.2) !important",
                  border: "1px solid rgba(255, 255, 255, 0.2) !important",
                  boxShadow: "none !important",
                  "&:checked": {
                    backgroundColor: "var(--color-brand-medium) !important",
                    borderColor: "var(--color-brand-medium) !important"
                  }
                }
              }}
            />
            <Text
              component="a"
              href="#resetpassword"
              onClick={() => setShowResetPassword(true)}
              c="dimmed"
              size="sm"
              style={{
                cursor: "pointer",
                color: "var(--color-text-medium)",
                textDecoration: "none",
                fontSize: "13px"
              }}
            >
              Reset password
            </Text>
          </Group>

          {error && !loading && <Text c="red" size="sm" mt="xs">{error}</Text>}

          <Button
            type="submit"
            disabled={loading}
            onClick={loginButtonClicked}
            fullWidth
            size="md"
            mt="lg"
            styles={{
              root: {
                backgroundColor: "#4A90E2",
                border: "none",
                borderRadius: "12px",
                height: "46px",
                fontSize: "15px",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "#357ABD"
                },
                "&:disabled": {
                  backgroundColor: "rgba(74, 144, 226, 0.5)"
                }
              }
            }}
          >
            {loading ? "Logging in..." : "Login"}
          </Button>
        </Stack>
      </form>

      <TextSeparator>
        <PrivacyLink />
      </TextSeparator>
      <Modal
        opened={showResetPassword}
        onClose={() => setShowResetPassword(false)}
        title="Reset password"
      >
        <ResetPasswordForm />
      </Modal>
    </>
  );
};

function mapStateToProps(state, ownProps) {
  return {};
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...authActions, getSelf }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(LoginForm);
